<template>
  <section class="section">
    <div class="container">
      <!-- Header -->
      <div class="has-text-centered mb-3">
        <img
          src="https://apimodul.gomodul.com/drive/static/50e00ba1-3fb3-4e7d-b04d-8bf7c4b2d8ca_tabsa.png"
          alt="TABSA Logo"
          style="
            max-width: 220px;
            margin-bottom: 1.5rem;
            display: block;
            margin-left: auto;
            margin-right: auto;
          "
        />
        <h1 class="title is-2">
          <span class="icon is-large">
            <i class="fa fa-credit-card"></i>
          </span>
          {{ $t("online loan application") }}
        </h1>
        <p class="subtitle is-5 has-text-grey">
          {{ $t("complete the form to request your loan") }}
        </p>
      </div>

      <!-- Progress Steps -->
      <div class="progress-steps mb-3">
        <div class="steps-container">
          <div
            class="step-item"
            :class="{
              'is-active': current_step >= 1,
              'is-completed': current_step > 1,
            }"
          >
            <div class="step-marker">
              <span class="step-icon">
                <i class="fa fa-user"></i>
              </span>
              <span class="step-number">1</span>
            </div>
            <div class="step-details">
              <p class="step-title">{{ $t("personal information") }}</p>
              <p class="step-description">{{ $t("basic personal data") }}</p>
            </div>
          </div>

          <div
            class="step-connector"
            :class="{ 'is-completed': current_step > 1 }"
          ></div>

          <div
            class="step-item"
            :class="{
              'is-active': current_step >= 2,
              'is-completed': current_step > 2,
            }"
          >
            <div class="step-marker">
              <span class="step-icon">
                <i class="fa fa-id-card"></i>
              </span>
              <span class="step-number">2</span>
            </div>
            <div class="step-details">
              <p class="step-title">{{ $t("identification") }}</p>
              <p class="step-description">{{ $t("identity documents") }}</p>
            </div>
          </div>

          <div
            class="step-connector"
            :class="{ 'is-completed': current_step > 2 }"
          ></div>

          <div
            class="step-item"
            :class="{
              'is-active': current_step >= 3,
              'is-completed': current_step > 3,
            }"
          >
            <div class="step-marker">
              <span class="step-icon">
                <i class="fa fa-camera"></i>
              </span>
              <span class="step-number">3</span>
            </div>
            <div class="step-details">
              <p class="step-title">{{ $t("verification") }}</p>
              <p class="step-description">{{ $t("photo verification") }}</p>
            </div>
          </div>

          <div
            class="step-connector"
            :class="{ 'is-completed': current_step > 3 }"
          ></div>

          <div
            class="step-item"
            :class="{
              'is-active': current_step >= 4,
              'is-completed': current_step > 4,
            }"
          >
            <div class="step-marker">
              <span class="step-icon">
                <i class="fa fa-money"></i>
              </span>
              <span class="step-number">4</span>
            </div>
            <div class="step-details">
              <p class="step-title">{{ $t("loan details") }}</p>
              <p class="step-description">{{ $t("amount and terms") }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Card -->
      <div class="card">
        <div class="card-content">
          <form @submit.prevent="submit_application">
            <!-- Step 1: Personal Information -->
            <div v-show="current_step === 1" class="step-content">
              <h2 class="title is-4 mb-4">
                <span class="icon">
                  <i class="fa fa-user has-text-info"></i>
                </span>
                {{ $t("personal information") }}
              </h2>

              <div class="field">
                <label class="label">
                  {{ $t("full name") }}
                  <span class="has-text-danger">*</span>
                </label>
                <div class="control has-icons-left">
                  <input
                    type="text"
                    class="input is-large"
                    v-model="application.full_name"
                    :class="{
                      'is-danger': !application.full_name && show_validation,
                    }"
                    :placeholder="$t('enter your full name')"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-user"></i>
                  </span>
                </div>
                <p
                  class="help is-danger"
                  v-if="!application.full_name && show_validation"
                >
                  {{ $t("full name is required") }}
                </p>
              </div>

              <div class="columns">
                <div class="column">
                  <div class="field">
                    <label class="label">
                      {{ $t("phone") }} <span class="has-text-danger">*</span>
                    </label>
                    <div class="control has-icons-left">
                      <input
                        type="tel"
                        class="input is-large"
                        v-model="application.phone"
                        :class="{
                          'is-danger': !application.phone && show_validation,
                        }"
                        :placeholder="$t('enter your phone number')"
                      />
                      <span class="icon is-small is-left">
                        <i class="fa fa-phone"></i>
                      </span>
                    </div>
                    <p
                      class="help is-danger"
                      v-if="!application.phone && show_validation"
                    >
                      {{ $t("phone is required") }}
                    </p>
                  </div>
                </div>
                <div class="column">
                  <div class="field">
                    <label class="label">
                      {{ $t("zip code") }}
                      <span class="has-text-danger">*</span>
                    </label>
                    <div class="control has-icons-left">
                      <input
                        type="text"
                        class="input is-large"
                        v-model="application.zip_code"
                        :class="{
                          'is-danger': !application.zip_code && show_validation,
                        }"
                        :placeholder="$t('enter your zip code')"
                      />
                      <span class="icon is-small is-left">
                        <i class="fa fa-map-marker"></i>
                      </span>
                    </div>
                    <p
                      class="help is-danger"
                      v-if="!application.zip_code && show_validation"
                    >
                      {{ $t("zip code is required") }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 2: Identification -->
            <div v-show="current_step === 2" class="step-content">
              <h2 class="title is-4 mb-4">
                <span class="icon">
                  <i class="fa fa-id-card has-text-info"></i>
                </span>
                {{ $t("identification document") }}
              </h2>

              <div class="field">
                <label class="label">
                  {{ $t("document type") }}
                  <span class="has-text-danger">*</span>
                </label>
                <div class="control">
                  <div class="select is-large is-fullwidth">
                    <select
                      v-model="application.document_type"
                      :class="{
                        'is-danger':
                          !application.document_type && show_validation,
                      }"
                    >
                      <option value="">{{ $t("select document type") }}</option>
                      <option value="dpi">{{ $t("dpi") }}</option>
                      <option value="passport">{{ $t("passport") }}</option>
                      <option value="birth_certificate">
                        {{ $t("birth certificate") }}
                      </option>
                    </select>
                  </div>
                </div>
                <p
                  class="help is-danger"
                  v-if="!application.document_type && show_validation"
                >
                  {{ $t("document type is required") }}
                </p>
              </div>

              <!-- Camera Instructions -->
              <div class="notification is-info is-light mb-4">
                <div class="media">
                  <div class="media-left">
                    <span class="icon is-large has-text-info">
                      <i class="fa fa-info-circle fa-2x"></i>
                    </span>
                  </div>
                  <div class="media-content">
                    <p class="title is-6">{{ $t("camera instructions") }}</p>
                    <p class="content">
                      {{
                        $t(
                          "the buttons below will activate your device camera to take photos directly"
                        )
                      }}.
                      {{
                        $t(
                          "make sure the documents are well lit and all text is clearly visible"
                        )
                      }}.
                    </p>
                  </div>
                </div>
              </div>

              <div class="columns">
                <div class="column">
                  <div class="field">
                    <label class="label">
                      {{ $t("document front photo") }}
                      <span class="has-text-danger">*</span>
                    </label>
                    <p class="help mb-3">
                      {{
                        $t("take a clear photo of the front of your document")
                      }}
                    </p>
                    <div class="file has-name is-fullwidth">
                      <label class="file-label">
                        <input
                          class="file-input"
                          type="file"
                          accept="image/*"
                          capture="environment"
                          @change="handle_file_upload($event, 'document_front')"
                        />
                        <span class="file-cta">
                          <span class="file-icon">
                            <i class="fa fa-camera"></i>
                          </span>
                          <span class="file-label">
                            {{ $t("take photo") }}
                          </span>
                        </span>
                        <span
                          class="file-name"
                          v-if="application.document_front"
                        >
                          {{ application.document_front.name }}
                        </span>
                      </label>
                    </div>
                    <div v-if="application.document_front_preview" class="mt-3">
                      <figure class="image is-256x256">
                        <img
                          :src="application.document_front_preview"
                          class="preview-image"
                        />
                      </figure>
                    </div>
                  </div>
                </div>

                <div class="column">
                  <div class="field">
                    <label class="label">
                      {{ $t("document back photo") }}
                      <span class="has-text-danger">*</span>
                    </label>
                    <p class="help mb-3">
                      {{
                        $t("take a clear photo of the back of your document")
                      }}
                    </p>
                    <div class="file has-name is-fullwidth">
                      <label class="file-label">
                        <input
                          class="file-input"
                          type="file"
                          accept="image/*"
                          capture="environment"
                          @change="handle_file_upload($event, 'document_back')"
                        />
                        <span class="file-cta">
                          <span class="file-icon">
                            <i class="fa fa-camera"></i>
                          </span>
                          <span class="file-label">
                            {{ $t("take photo") }}
                          </span>
                        </span>
                        <span
                          class="file-name"
                          v-if="application.document_back"
                        >
                          {{ application.document_back.name }}
                        </span>
                      </label>
                    </div>
                    <div v-if="application.document_back_preview" class="mt-3">
                      <figure class="image is-256x256">
                        <img
                          :src="application.document_back_preview"
                          class="preview-image"
                        />
                      </figure>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Verification -->
            <div v-show="current_step === 3" class="step-content">
              <h2 class="title is-4 mb-4">
                <span class="icon">
                  <i class="fa fa-camera has-text-info"></i>
                </span>
                {{ $t("selfie verification") }}
              </h2>

              <div class="has-text-centered">
                <div class="field">
                  <label class="label">
                    {{ $t("selfie photo") }}
                    <span class="has-text-danger">*</span>
                  </label>
                  <p class="help">
                    {{
                      $t("take a clear selfie photo for identity verification")
                    }}
                  </p>

                  <div
                    class="file has-name is-large"
                    style="justify-content: center"
                  >
                    <label class="file-label">
                      <input
                        class="file-input"
                        type="file"
                        accept="image/*"
                        capture="user"
                        @change="handle_file_upload($event, 'selfie')"
                      />
                      <span class="file-cta">
                        <span class="file-icon">
                          <i class="fa fa-camera"></i>
                        </span>
                        <span class="file-label">
                          {{ $t("take selfie") }}
                        </span>
                      </span>
                      <span class="file-name" v-if="application.selfie">
                        {{ application.selfie.name }}
                      </span>
                    </label>
                  </div>

                  <div v-if="application.selfie_preview" class="mt-4">
                    <figure class="image is-256x256" style="margin: 0 auto">
                      <img
                        :src="application.selfie_preview"
                        class="preview-image"
                      />
                    </figure>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 4: Loan Details -->
            <div v-show="current_step === 4" class="step-content">
              <h2 class="title is-4 mb-4">
                <span class="icon">
                  <i class="fa fa-money has-text-info"></i>
                </span>
                {{ $t("loan details") }}
              </h2>

              <div class="columns">
                <div class="column is-6">
                  <div class="field">
                    <label class="label">
                      {{ $t("requested amount") }}
                      <span class="has-text-danger">*</span>
                    </label>
                    <div class="control has-icons-left">
                      <input
                        type="number"
                        class="input is-large"
                        v-model="application.requested_amount"
                        :class="{
                          'is-danger':
                            !application.requested_amount && show_validation,
                        }"
                        :placeholder="$t('enter amount')"
                        step="0.01"
                        min="1"
                      />
                      <span class="icon is-small is-left">
                        <i class="fa fa-dollar"></i>
                      </span>
                    </div>
                    <p
                      class="help is-danger"
                      v-if="!application.requested_amount && show_validation"
                    >
                      {{ $t("requested amount is required") }}
                    </p>
                  </div>
                </div>

                <div class="column is-6">
                  <div class="amount-display">
                    <p class="title is-3 has-text-success">
                      {{ format_currency(application.requested_amount || 0) }}
                    </p>
                    <p class="subtitle is-6">{{ $t("requested amount") }}</p>
                  </div>
                </div>
              </div>

              <!-- Terms and Conditions -->
              <div class="field">
                <input
                  class="is-checkradio"
                  type="checkbox"
                  id="chk_accept_terms"
                  name="chk_accept_terms"
                  v-model="application.accept_terms"
                />
                <label for="chk_accept_terms"
                  >{{ $t("i accept the") }}
                  <a href="#" @click.prevent="show_terms = true">{{
                    $t("terms and conditions")
                  }}</a></label
                >
              </div>
              <!-- <div class="field mt-5">
                <div class="control">
                  <label class="checkbox">
                    <input type="checkbox" v-model="application.accept_terms" />
                    {{ $t("I accept the") }}
                    <a href="#" @click.prevent="show_terms = true">{{
                      $t("terms and conditions")
                    }}</a>
                  </label>
                </div>
                <p
                  class="help is-danger"
                  v-if="!application.accept_terms && show_validation"
                >
                  {{ $t("you must accept the terms and conditions") }}
                </p>
              </div> -->
            </div>

            <!-- Navigation Buttons -->
            <div class="field is-grouped is-grouped-centered mt-6">
              <div class="control">
                <button
                  type="button"
                  class="button is-large"
                  @click="previous_step"
                  v-if="current_step > 1"
                >
                  <span class="icon">
                    <i class="fa fa-arrow-left"></i>
                  </span>
                  <span>{{ $t("previous") }}</span>
                </button>
              </div>

              <div class="control">
                <button
                  type="button"
                  class="button is-info is-large"
                  @click="next_step"
                  v-if="current_step < 4"
                  :disabled="!is_current_step_valid"
                >
                  <span>{{ $t("next") }}</span>
                  <span class="icon">
                    <i class="fa fa-arrow-right"></i>
                  </span>
                </button>

                <button
                  type="submit"
                  class="button is-success is-large"
                  v-if="current_step === 4"
                  :disabled="!is_form_valid || submitting"
                  :class="{ 'is-loading': submitting }"
                >
                  <span class="icon">
                    <i class="fa fa-paper-plane"></i>
                  </span>
                  <span>{{ $t("submit application") }}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- Success Message -->
      <div class="modal" :class="{ 'is-active': show_success }">
        <div class="modal-background"></div>
        <div class="modal-card">
          <header class="modal-card-head has-background-success">
            <p class="modal-card-title has-text-white">
              <span class="icon">
                <i class="fa fa-check-circle"></i>
              </span>
              {{ $t("application submitted") }}
            </p>
          </header>
          <section class="modal-card-body has-text-centered">
            <div class="content">
              <p class="title is-4 has-text-success">
                {{ $t("congratulations") }}!
              </p>
              <p>
                {{
                  $t("your loan application has been submitted successfully")
                }}
              </p>
              <p>{{ $t("we will contact you soon with updates") }}</p>
            </div>
          </section>
          <footer class="modal-card-foot">
            <button class="button is-success" @click="reset_form">
              {{ $t("submit another application") }}
            </button>
          </footer>
        </div>
      </div>

      <!-- Error Message -->
      <div class="notification is-danger mt-4" v-if="error">
        <button class="delete" @click="error = ''"></button>
        <div v-html="error"></div>
      </div>
    </div>
  </section>
</template>

<script>
import { post, generate_error_message_text, format_currency } from "@/utils";

export default {
  name: "OnlineLoanApplication",
  data() {
    return {
      current_step: 1,
      show_validation: false,
      submitting: false,
      show_success: false,
      show_terms: false,
      error: "",
      application: {
        full_name: "",
        phone: "",
        zip_code: "",
        document_type: "",
        document_front: null,
        document_front_preview: null,
        document_back: null,
        document_back_preview: null,
        selfie: null,
        selfie_preview: null,
        requested_amount: "",
        accept_terms: false,
      },
    };
  },
  computed: {
    is_step_1_valid() {
      return (
        this.application.full_name &&
        this.application.phone &&
        this.application.zip_code
      );
    },
    is_step_2_valid() {
      return (
        this.application.document_type &&
        this.application.document_front &&
        this.application.document_back
      );
    },
    is_step_3_valid() {
      return this.application.selfie;
    },
    is_step_4_valid() {
      return (
        this.application.requested_amount &&
        this.application.requested_amount > 0 &&
        this.application.accept_terms
      );
    },
    is_current_step_valid() {
      switch (this.current_step) {
        case 1:
          return this.is_step_1_valid;
        case 2:
          return this.is_step_2_valid;
        case 3:
          return this.is_step_3_valid;
        case 4:
          return this.is_step_4_valid;
        default:
          return false;
      }
    },
    is_form_valid() {
      return (
        this.is_step_1_valid &&
        this.is_step_2_valid &&
        this.is_step_3_valid &&
        this.is_step_4_valid
      );
    },
  },
  methods: {
    format_currency,
    next_step() {
      this.show_validation = true;
      if (this.is_current_step_valid && this.current_step < 4) {
        this.current_step++;
        this.show_validation = false;
      }
    },
    previous_step() {
      if (this.current_step > 1) {
        this.current_step--;
        this.show_validation = false;
      }
    },
    handle_file_upload(event, field) {
      const file = event.target.files[0];
      if (file) {
        this.application[field] = file;

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.application[field + "_preview"] = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    async submit_application() {
      this.show_validation = true;

      if (!this.is_form_valid) {
        this.error = this.$t("please complete all required fields");
        return;
      }

      this.submitting = true;
      this.error = "";

      try {
        // Create FormData for file uploads
        const formData = new FormData();

        // Add text fields
        formData.append("full_name", this.application.full_name);
        formData.append("phone", this.application.phone);
        formData.append("zip_code", this.application.zip_code);
        formData.append("document_type", this.application.document_type);
        formData.append("requested_amount", this.application.requested_amount);

        // Add files
        if (this.application.document_front) {
          formData.append("document_front", this.application.document_front);
        }
        if (this.application.document_back) {
          formData.append("document_back", this.application.document_back);
        }
        if (this.application.selfie) {
          formData.append("selfie", this.application.selfie);
        }

        // Submit application
        await post("/loan/application/online", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.show_success = true;
      } catch (error) {
        this.error = generate_error_message_text(error);
      } finally {
        this.submitting = false;
      }
    },
    reset_form() {
      this.current_step = 1;
      this.show_validation = false;
      this.show_success = false;
      this.error = "";
      this.application = {
        full_name: "",
        phone: "",
        zip_code: "",
        document_type: "",
        document_front: null,
        document_front_preview: null,
        document_back: null,
        document_back_preview: null,
        selfie: null,
        selfie_preview: null,
        requested_amount: "",
        accept_terms: false,
      };
    },
  },
  mounted() {
    setTimeout(() => {
      window.jQuery(".navbar").hide();
    }, 500);
  },
};
</script>

<style scoped>
/* Progress Steps Component */
.progress-steps {
  padding: 2rem 0;
}

.steps-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  max-width: 800px;
  margin: 0 auto;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  flex: 1;
  max-width: 180px;
}

.step-marker {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  /* background: #f5f5f5; */
  border: 3px solid #dbdbdb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.step-icon {
  font-size: 1.5rem;
  color: #999;
  transition: all 0.3s ease;
}

.step-number {
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e31f29;
  color: rgb(255, 255, 255);
  font-size: 0.75rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 1px solid #c2c2c2;
}

.step-details {
  max-width: 140px;
}

.step-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #4a4a4a;
  margin: 0 0 0.25rem 0;
  line-height: 1.2;
}

.step-description {
  font-size: 0.75rem;
  color: #999;
  margin: 0;
  line-height: 1.2;
}

.step-connector {
  flex: 1;
  height: 3px;
  background: #dbdbdb;
  margin: 0 1rem;
  border-radius: 3px;
  transition: all 0.3s ease;
  position: relative;
  top: -40px;
}

/* Active State */
.step-item.is-active .step-marker {
  background: #e31f29;
  border-color: #e31f29;
  transform: scale(1.1);
  box-shadow: 0 4px 20px rgba(0, 209, 178, 0.3);
}

.step-item.is-active .step-icon {
  color: white;
  font-size: 1.8rem;
}

.step-item.is-active .step-number {
  background: #252525;
  color: #ffffff;
  border: 1px solid #e31f29;
  transform: scale(1.1);
}

.step-item.is-active .step-title {
  color: #e31f29;
  font-weight: 700;
}

.step-item.is-active .step-description {
  color: #adadad;
}

/* Completed State */
.step-item.is-completed .step-marker {
  background: linear-gradient(135deg, #23d160, #20bc56);
  border-color: #23d160;
  box-shadow: 0 4px 20px rgba(35, 209, 96, 0.3);
}

.step-item.is-completed .step-icon {
  color: white;
}

.step-item.is-completed .step-number {
  /* background: #ffffff; */
  color: #23d160;
}

.step-item.is-completed .step-title {
  color: #23d160;
  font-weight: 700;
}

.step-item.is-completed .step-description {
  color: #666;
}

.step-connector.is-completed {
  background: linear-gradient(90deg, #23d160, #00d1b2);
  box-shadow: 0 2px 8px rgba(0, 209, 178, 0.2);
}

/* Form Styles */
.step-content {
  /* min-height: 400px; */
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.card {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.preview-image {
  border-radius: 8px;
  object-fit: cover;
  width: 100%;
  height: 100%;
  border: 2px solid #dbdbdb;
}

.amount-display {
  text-align: center;
  padding: 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: white;
}

.amount-display .title {
  color: white !important;
  margin-bottom: 0.5rem;
}

.amount-display .subtitle {
  color: rgba(255, 255, 255, 0.8) !important;
}

/* File Upload Styles */
.file-cta {
  border-radius: 8px;
  border: 2px dashed #dbdbdb;
  /* background: #fafafa; */
  transition: all 0.3s ease;
}

.file-cta:hover {
  border-color: #00d1b2;
  /* background: #f0fffe; */
}

.file.has-name .file-name {
  max-width: 200px;
  border-radius: 0 8px 8px 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .steps-container {
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 0 1rem;
    gap: 0;
  }

  .step-connector {
    flex: 0 0 30px;
    height: 2px;
    margin: 0 0.5rem;
    top: -30px;
  }

  .step-item {
    flex: 0 0 auto;
    min-width: 100px;
    max-width: 120px;
  }

  .step-marker {
    width: 50px;
    height: 50px;
  }

  .step-icon {
    font-size: 1rem;
  }

  .step-number {
    width: 18px;
    height: 18px;
    font-size: 0.6rem;
    bottom: -3px;
    right: -3px;
  }

  .step-details {
    max-width: 100px;
  }

  .step-title {
    font-size: 0.75rem;
    line-height: 1.1;
  }

  .step-description {
    font-size: 0.65rem;
    line-height: 1.1;
  }

  .step-content {
    min-height: auto;
  }

  .columns {
    display: block;
  }

  .column {
    width: 100% !important;
  }
}

@media (max-width: 480px) {
  .progress-steps {
    padding: 1rem 0;
  }

  .steps-container {
    padding: 0 0.5rem;
  }

  .step-connector {
    flex: 0 0 20px;
    margin: 0 0.25rem;
  }

  .step-item {
    min-width: 80px;
    max-width: 90px;
  }

  .step-marker {
    width: 40px;
    height: 40px;
  }

  .step-icon {
    font-size: 0.875rem;
  }

  .step-number {
    width: 16px;
    height: 16px;
    font-size: 0.55rem;
    bottom: -2px;
    right: -2px;
  }

  .step-title {
    font-size: 0.7rem;
  }

  .step-description {
    font-size: 0.6rem;
  }
}

/* Success Animation */
.modal-card {
  animation: bounceIn 0.5s ease-out;
}

@keyframes bounceIn {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Loading Button */
.button.is-loading::after {
  border-color: transparent transparent white white !important;
}

/* Validation States */
.input.is-danger,
.select.is-danger {
  border-color: #ff3860;
  box-shadow: 0 0 0 0.125em rgba(255, 56, 96, 0.25);
}

.help.is-danger {
  color: #ff3860;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Custom Checkbox */
.checkbox input[type="checkbox"] {
  margin-right: 0.5rem;
}

/* Form Transitions */
.control,
.field {
  transition: all 0.3s ease;
}

.input:focus,
.select select:focus {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 209, 178, 0.3);
}
</style>
